import * as Sentry from '@sentry/react';
import { useSelectedOrganization } from '../features/organization/hooks/useSelectedOrganization';
import { normalizeHashRoute } from './sentry-route';

/**
 * A main-thread stall long enough to be the "dashboard froze" an operator
 * reports. Browsers show their own "page unresponsive" prompt a few seconds
 * later than this, so 2 s captures the event before the tab is killed.
 */
export const LONG_TASK_THRESHOLD_MS = 2000;

/** Sentry quota discipline: at most one stall report per tab per minute. */
export const MIN_REPORT_INTERVAL_MS = 60_000;

/**
 * Two long tasks separated by less than this are ONE freeze.
 *
 * The browser emits one `longtask` entry per TASK, and the thing an operator
 * calls a freeze is normally a RUN of them — a render storm, or a chain of
 * synchronous recomputes each triggered by the last one's state update. The
 * gap between consecutive tasks in such a run is the few milliseconds the
 * event loop needs to pick up the next one, so a tolerance an order of
 * magnitude above that merges a real run without ever merging two freezes
 * that were genuinely seconds apart.
 */
export const LONG_TASK_GAP_TOLERANCE_MS = 100;

/**
 * One `longtask` entry, reduced to the two fields that locate it in time.
 *
 * `startTime` is OPTIONAL and that is load-bearing rather than defensive: an
 * entry without one cannot be placed on the timeline, so it is never merged
 * with a neighbour and stands as its own span. That keeps a caller which has
 * only durations (the old shape of this module) reporting exactly what it
 * reported before, instead of silently merging unrelated tasks.
 */
export interface LongTaskEntry {
    duration: number;
    startTime?: number;
}

export interface LongTaskReport {
    /** Worst CONTIGUOUS blocked span in the batch — what the operator sat through. */
    durationMs: number;
    /** Worst SINGLE task inside that span. */
    longestTaskMs: number;
    /** How many long tasks made up that span. 1 = one huge synchronous call. */
    taskCount: number;
    route: string;
    organizationId?: string;
}

export interface LongTaskHandlerDeps {
    capture: (report: LongTaskReport) => void;
    now?: () => number;
    getRoute?: () => string;
    getOrganizationId?: () => string | undefined;
    threshold?: number;
    minInterval?: number;
    gapTolerance?: number;
}

interface BlockedSpan {
    durationMs: number;
    longestTaskMs: number;
    taskCount: number;
}

/**
 * The worst run of back-to-back long tasks in a batch.
 *
 * Why this is not `Math.max(entry.duration)`, which is what this module did
 * until run 208 — and why it is not a sum either, which is the obvious fix and
 * the wrong one:
 *
 *   - `Math.max` answers "what was the longest single task", and a freeze made
 *     of several consecutive tasks is reported as its largest PIECE.
 *     `DASHBOARD-45` was filed as a 14,177 ms stall on `/admin/organization`
 *     while its own breadcrumbs show two separate multi-second stalls inside
 *     one freeze: four independent requests, issued 25 ms apart, all drained
 *     their `[fetch]` breadcrumb at the identical millisecond `07:07:23.230`,
 *     and the next breadcrumb is 24.7 s after that. Four independent responses
 *     do not arrive in the same millisecond; a blocked thread releasing their
 *     callbacks at once does.
 *   - It also misses a freeze made entirely of SUB-threshold tasks. Thirty
 *     consecutive 80 ms renders is 2.4 s of dead UI and not one entry in it
 *     clears 2,000 ms.
 *   - Summing the batch is wrong because `observe({ buffered: true })` hands
 *     the first callback every long task since page load, which can span
 *     minutes. Contiguity is what separates "one freeze" from "a page's whole
 *     history", so the gap test is the fix and the sum is a bug wearing its
 *     clothes.
 */
export function worstBlockedSpan(
    entries: ReadonlyArray<LongTaskEntry>,
    gapTolerance: number = LONG_TASK_GAP_TOLERANCE_MS,
): BlockedSpan {
    let best: BlockedSpan = { durationMs: 0, longestTaskMs: 0, taskCount: 0 };
    const consider = (span: BlockedSpan) => {
        if (span.durationMs > best.durationMs) best = span;
    };

    for (const entry of entries) {
        if (typeof entry.startTime !== 'number') {
            consider({ durationMs: entry.duration, longestTaskMs: entry.duration, taskCount: 1 });
        }
    }

    const placed = entries
        .filter((entry): entry is LongTaskEntry & { startTime: number } => typeof entry.startTime === 'number')
        .slice()
        .sort((a, b) => a.startTime - b.startTime);

    let open = false;
    let spanStart = 0;
    let spanEnd = 0;
    let longestTaskMs = 0;
    let taskCount = 0;
    const flush = () => {
        if (open) consider({ durationMs: spanEnd - spanStart, longestTaskMs, taskCount });
    };

    for (const entry of placed) {
        const end = entry.startTime + entry.duration;
        if (open && entry.startTime <= spanEnd + gapTolerance) {
            spanEnd = Math.max(spanEnd, end);
            longestTaskMs = Math.max(longestTaskMs, entry.duration);
            taskCount += 1;
        } else {
            flush();
            open = true;
            spanStart = entry.startTime;
            spanEnd = end;
            longestTaskMs = entry.duration;
            taskCount = 1;
        }
    }
    flush();

    return best;
}

/**
 * Pure core of the reporter: given long-task entries, decide whether to report.
 * Separated from the browser wiring so the threshold, the throttle and the
 * contiguity merge are unit-tested.
 */
export function createLongTaskHandler({
    capture,
    now = () => Date.now(),
    getRoute = () => normalizeHashRoute(window.location.hash),
    getOrganizationId = () => useSelectedOrganization.getState().selectedOrganization?.id,
    threshold = LONG_TASK_THRESHOLD_MS,
    minInterval = MIN_REPORT_INTERVAL_MS,
    gapTolerance = LONG_TASK_GAP_TOLERANCE_MS,
}: LongTaskHandlerDeps): (entries: ReadonlyArray<LongTaskEntry>) => void {
    let lastReportAt = -Infinity;
    return (entries) => {
        const worst = worstBlockedSpan(entries, gapTolerance);
        if (worst.durationMs < threshold) return;
        const t = now();
        if (t - lastReportAt < minInterval) return;
        lastReportAt = t;
        capture({
            durationMs: Math.round(worst.durationMs),
            longestTaskMs: Math.round(worst.longestTaskMs),
            taskCount: worst.taskCount,
            route: getRoute(),
            organizationId: getOrganizationId(),
        });
    };
}

/**
 * Send one stall to Sentry: a warning, grouped per route, tagged for filtering.
 *
 * The duration is in the MESSAGE and the fingerprint is route-only, so
 * changing how the duration is measured cannot split or re-group an existing
 * issue — `DASHBOARD-45`/`-46`/`-47` keep their groups.
 */
export function captureLongTask(report: LongTaskReport): void {
    Sentry.captureMessage(`Main thread blocked for ${report.durationMs} ms on ${report.route}`, {
        level: 'warning',
        fingerprint: ['dashboard-long-task', report.route],
        tags: {
            'dashboard.route': report.route,
            'dashboard.organizationId': report.organizationId ?? 'none',
            'longtask.bucket': report.durationMs >= 10_000 ? '10s+' : report.durationMs >= 5_000 ? '5-10s' : '2-5s',
            /*
             * Which of the two causes this was, without needing a profile: one
             * enormous synchronous call, or a storm of smaller ones. They want
             * different fixes and the duration alone does not tell them apart.
             */
            'longtask.shape': report.taskCount > 1 ? 'multi-task' : 'single-task',
        },
        extra: {
            durationMs: report.durationMs,
            longestTaskMs: report.longestTaskMs,
            taskCount: report.taskCount,
        },
    });
}

/**
 * Report multi-second main-thread stalls to Sentry, with the route and the
 * selected organization, so a freeze that never throws still leaves evidence.
 *
 * Why this exists: the org-switch freeze platform admins hit for months never
 * appeared in Sentry — a hung tab sends nothing, and when it recovers nothing
 * has thrown. The browser's Long Tasks API sees the stall as soon as the thread
 * is free again, and Sentry's default fetch breadcrumbs already record which
 * requests preceded it, so the event answers "which page, which org, after
 * which calls" without any further instrumentation.
 *
 * No-op where the Long Tasks API is missing (Safari, Firefox). Returns a
 * disposer; the app never calls it, tests do.
 */
export function startLongTaskReporter(): () => void {
    if (typeof PerformanceObserver === 'undefined') return () => {};
    const supported = (PerformanceObserver as unknown as { supportedEntryTypes?: string[] }).supportedEntryTypes;
    if (!supported || !supported.includes('longtask')) return () => {};
    const handle = createLongTaskHandler({ capture: captureLongTask });
    try {
        const observer = new PerformanceObserver((list) => handle(list.getEntries()));
        observer.observe({ type: 'longtask', buffered: true });
        return () => observer.disconnect();
    } catch {
        return () => {};
    }
}
